import { api } from './api.js';

export interface UploadResult {
  evidenceId: string;
  extractionStatus: string;
}

export async function uploadDocument(
  file: File,
  claimId: string,
  onProgress?: (pct: number) => void,
): Promise<UploadResult> {
  // 1. Get presigned PUT URL
  const { url, key } = await api.post<{ url: string; key: string; expiresInSeconds: number }>(
    '/uploads/signed-url',
    { fileName: file.name, contentType: file.type || 'application/octet-stream', claimId },
  );

  // 2. PUT directly to S3 (no auth headers — presigned URL handles auth)
  await uploadToS3(url, file, onProgress);

  // 3. Register with the API
  const result = await api.post<UploadResult>(`/claims/${claimId}/evidence`, {
    key,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
  });

  return result;
}

async function uploadToS3(
  presignedUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('S3 PUT network error'));
    xhr.send(file);
  });
}
